import React, { Fragment, useEffect, useRef, useState } from 'react';

import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';

import Header from '../components/Header';
import VehiclesTable from '../components/VehiclesTable';
import AdminSearch from '../components/AdminSearch';
import Popup from '../components/Popup';
import '../styles/_dashboard.scss';
import DashboardMembershipsTable from '../components/DashboardMembershipsTable';

const Dashboard = ({
  user,
  token,
  detailsLoading,
  dashboardAttemptError,
  showDashboardAccountDetails,
  onSaveUserDetails,
  dashboardPasswordError,
  showDashboardPassword,
  onSaveNewPassword,
  onLogout
}) => {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [dataError, setDataError] = useState('');
  const [tiers, setTiers] = useState([]);
  const userRef = useRef()

  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const options = [
    'Ms.', 'Mrs.', 'Mr.'
  ];
  const [attemptError, setAttemptError] = useState('');
  const [formData, setFormData] = useState({
    prefix: "",
    firstName: "",
    lastName: "",
    email: "",
  })

  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordData, setPasswordData] = useState({
    existingPassword: "",
    newPassword: "",
    confirmPassword: ""
  })

  const [showSubInfo, setShowSubInfo] = useState(false);
  const [showVehicleInfo, setShowVehicleInfo] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      userRef.current = user;
      
      getMyVehicles();
      getAvailableTiers();
      getMyMemberships();

      setFormData({
        ...formData,
        prefix: user.prefix,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email
      })
    }
  }, [user])

  useEffect(() => {
    if (showSubInfo || showVehicleInfo) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [showSubInfo, showVehicleInfo])

  useEffect(() => {
    setAttemptError(dashboardAttemptError)
  }, [dashboardAttemptError])

  useEffect(() => {
    setShowAccountDetails(showDashboardAccountDetails)
  }, [showDashboardAccountDetails])

  useEffect(() => {
    setPasswordError(dashboardPasswordError)
  }, [dashboardPasswordError])

  useEffect(() => {
    setShowPassword(showDashboardPassword)
  }, [showDashboardPassword])

  const getMyVehicles = () => {
    setLoading(true);
    setDataError('');

    fetch(`/api/vehicles/${userRef.current.id}`)
      .then(res => res.json())
      .then(
        (result) => {
          if (result.error) {
            setDataError('There was a problem loading data.')
            setLoading(false)
          } else {
            mapResults(result)
            setLoading(false)
          }
        },
        (error) => {
          console.log(error)
          setDataError('There was a problem loading data.')
          setLoading(false)
        }
    )
  }

  const [tiersLoading, setTiersLoading] = useState(false);
  const getAvailableTiers = () => {
    setTiersLoading(true);
    setDataError('');

    fetch(`/api/tiers`)
      .then(res => res.json())
      .then(
        (result) => {
          if (result.error) {
            console.log(result.error);
            setTiersLoading(false)
          } else {
            setTiers(result)
            setTiersLoading(false)
          }
        },
        (error) => {
          console.log(error)
          setTiersLoading(false)
        }
    )

    setLoading(false);
  }

  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [membershipsError, setMembershipsError]  = useState('');
  const getMyMemberships = async () => {
    setLoading(true);
    setDataError('');

    const rawResponse = await fetch(`/api/subscriptions/${userRef.current.id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    const response = await rawResponse.json()

    setMemberships(response)
    setLoadingMemberships(false)
    setLoading(false);
  }

  const mapResults = (result) => {
    if (result && !result.error && result[0]) {
      const mappedResults = result.map(r => {
        let membership = '';
        if (!r.subscriptionId || r.status !== 'ACTIVE') {
          membership = 'Unassigned';
        } else {
          membership = r.subscription.subscriptionTier.name;
        }

        return {
          memberNumber: r.memberNumber,
          membership,
          year: r.year,
          make: parseMake(r.make),
          model: r.model,
          licensePlate: r.licensePlate,
          color: r.color,
          email: r.user ? r.user.email : '',
          subscriptionId: r.subscriptionId,
          tierId: r.subscription ? r.subscription.tierId : undefined,
          status: r.status
        }
      })
      setResults(mappedResults)
    }
  }

  const parseMake = (make) => {
    if (make === 'Volkswagen') {
      return 'VW'
    }
    return make;
  }
  
  // regular inputs in account details popup
  const onChange = (e) => {
    setAttemptError('')
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  // account details popup dropdown
  const onChangePrefix = (e) => {
    setFormData({
      ...formData,
      prefix: e.value
    })
  }

  // change password popup
  const onChangePassword = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    })
  }

  const saveUserDetails = () => {
    onSaveUserDetails(formData)
  }

  const saveNewPassword = () => {
    setPasswordError('')
    if (passwordData.newPassword.replace(/\s/g, '') === '' || passwordData.confirmPassword.replace(/\s/g, '') === '') {
      setPasswordError('Please enter and confirm a new password.')
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New and confirmed password do not match.')
    } else {
      onSaveNewPassword(passwordData)
    }
  }

  const onAddSubscription = () => {
    getMyVehicles();
    getMyMemberships();
  }

  return (
    <div className="dashboard">
      <Popup
        showPopup={showAccountDetails}
        loading={detailsLoading}
        setShowPopup={setShowAccountDetails}
        user={user}
        title={'Change Account Details'}
        buttonTitle={'Save'}
        buttonAction={saveUserDetails}
        error={attemptError}
      >
        <Dropdown
          options={options}
          onChange={(e) => onChangePrefix(e)}
          value={formData.prefix}
          placeholder="Select an prefix"
          className="custom-dropdown"
        />
        <input type="text" placeholder="enter your first name" name="firstName" value={formData.firstName} onChange={(e) => onChange(e)} />
        <input type="text" placeholder="enter your last name" name="lastName" value={formData.lastName} onChange={(e) => onChange(e)} className={formData.lastName === '' && (attemptError !== '') ? 'error' : ''} />
        <input type="text" placeholder="enter your email" name="email" value={formData.email} onChange={(e) => onChange(e)} className={formData.email === '' && (attemptError !== '') ? 'error' : ''}/>
      </Popup>

      <Popup
        showPopup={showPassword}
        loading={detailsLoading}
        setShowPopup={setShowPassword}
        user={user}
        title={'Change Password'}
        buttonTitle={'Save'}
        buttonAction={saveNewPassword}
        error={passwordError}
      >
        <input type="password" placeholder="enter existing password" name="existingPassword" value={passwordData.existingPassword} onChange={(e) => onChangePassword(e)} />
        <input type="password" placeholder="enter new password" name="newPassword" value={passwordData.newPassword} onChange={(e) => onChangePassword(e)} />
        <input type="password" placeholder="enter confirm password" name="confirmPassword" value={passwordData.confirmPassword} onChange={(e) => onChangePassword(e)} className={passwordData.newPassword !== passwordData.confirmPassword && (passwordError !== '') ? 'error' : ''}/>
      </Popup>

      <Popup
        showPopup={showSubInfo}
        loading={false}
        setShowPopup={setShowSubInfo}
        title={'Membership Subscriptions'}
        hideSubmit={true}
      > 
        <div className="info-block">
          <p className="tier-description question">What is it?</p>
          <p className="tier-description">Membership subscriptions are monthly, unlimited services available to your vehicle(s).</p>
          <p className="tier-description question">Does the price shown include tax?</p>
          <p className="tier-description">No. Tax is not included on our website - a 6% sales tax will be applied to each subscription via PayPal.</p>
          <p className="tier-description question">How do I add a vehicle to an existing membership?</p>
          <p className="tier-description">Due to limitations with PayPal, you cannot edit existing memberships. You will have to cancel the membership and create a new one with all applicable vehicles.</p>
          <p className="tier-description question">Why does my membership say "CANCELLED"?</p>
          <p className="tier-description">Memberships marked "CANCELLED" were cancelled from your <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">PayPal Dashboard</a> instead of our website. Once cancelled, you can create a new subscription for those vehicles. Records will continue to appear until past the expiration date. You can also cancel from our dashboard, and we'll stop any PayPal payments for you!</p>
          <p className="tier-description question">What does it mean for my subscription to expire?</p>
          <p className="tier-description">Membership subscriptions are auto-renewing monthly subscriptions. The only way for a subscription to expire (and not be cancelled) would be if your payment method failed through PayPal. Verify and update any payment methods <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">here</a>.</p>
          <p className="tier-description question">Why am I being charged for a subscription I don't see here?</p>
          <p className="tier-description">Although rare, this can happen if we were able to create a subscription with <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">PayPal</a> but were not able to create one in our own system. Please <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">cancel your subscription</a> and try again.</p>
        </div>
      </Popup>

      <Popup
        showPopup={showVehicleInfo}
        loading={false}
        setShowPopup={setShowVehicleInfo}
        title={'Vehicles'}
        hideSubmit={true}
      > 
        <div className="info-block">
          <p className="tier-description question">What is it?</p>
          <p className="tier-description">Any vehicle(s) listed on your dashboard can be added to a Quik Quality membership.</p>
          <p className="tier-description question">What if my car doesn't have a membership?</p>
          <p className="tier-description">Only vehicles associated with an <b>active</b> Quik Quality membership will be able to enter our carwash without payment.</p>
          <p className="tier-description question">Can I add vehicles for other family members?</p>
          <p className="tier-description">Of course! Your family is our family. We just ask that you limit vehicles to your household.</p>
          <p className="tier-description question">Can I remove a vehicle that I no longer own?</p>
          <p className="tier-description">Not at this time.</p>
        </div>
      </Popup>

      <Header user={user} onLogout={onLogout}></Header>
      <AdminSearch user={user}></AdminSearch>
      
      <div className="welcome-message">
        <h4>Welcome Back</h4>
        <h2>{user.prefix} {user.lastName}</h2>
      </div>

      <div className="user-details">
        <div><b>Full Name:</b> {`${user.prefix}${user.firstName ? ` ${user.firstName}` : ''} ${user.lastName}`}</div>
        <div><b>Email:</b> {user.email}</div>

        <div className="button" onClick={() => setShowAccountDetails(true)}>Change Account Details</div>
        <div className="button" onClick={() => setShowPassword(true)}>Change Password</div>

        {!user.isAdmin &&
          <>
            <div className="table-title">
            <h2>My Vehicles</h2>
              <i className={`fa fa-question-circle`} onClick={() => setShowVehicleInfo(true)}></i>
            </div>
            
            <VehiclesTable
              user={user} 
              vehicles={results} 
              dashboard={true} 
              loading={loading || !token} 
              dataError={dataError} 
              getMyVehicles={getMyVehicles}>
            </VehiclesTable>

            <div className="table-title">
              <h2>My Membership Subscriptions</h2>
              <i className={`fa fa-question-circle`} onClick={() => setShowSubInfo(true)}></i>
            </div>
            
            <DashboardMembershipsTable
              user={user}
              tiers={tiers}
              token={token}
              vehicles={results}
              memberships={memberships}
              loading={loadingMemberships || tiersLoading || !token}
              dataError={membershipsError}
              onAddSubscription={onAddSubscription}
            ></DashboardMembershipsTable>
          </>
        }

        {user.isAdmin &&
          <Fragment>
            <div style={{marginTop: '2rem', maxWidth: '700px'}}>Because admin are only able to scan for memberships in the mobile app, you must have a regular account for adding personal subscriptions and vehicles.</div>
          </Fragment>
        }
      </div>
    </div>
  )
}

export default Dashboard;